import { Howl } from 'howler';
/**
 * Audio Helper Module
 *
 * This module provides a comprehensive audio management system using Howler.js.
 *
 * Features:
 * - Play, pause, stop audio by key
 * - Mute/unmute all audios
 * - Automatic pause/resume on tab focus/blur
 * - Prevents playing audio that has ended
 * - Volume control per audio
 *
 * Usage Example:
 * ```typescript
 * // Initialize audio system (usually done in app initialization)
 * import { initializeAudio } from './helper/audioHelper';
 * import { fetchData } from './helper/helper';
 *
 * const audioConfig = await fetchData('./assets/data/audios.json');
 * initializeAudio(audioConfig);
 *
 * // Play audio
 * import { playAudio } from './helper/audioHelper';
 * playAudio('example_audio');
 *
 * // Pause audio
 * import { pauseAudio } from './helper/audioHelper';
 * pauseAudio('example_audio');
 *
 * // Stop audio
 * import { stopAudio } from './helper/audioHelper';
 * stopAudio('example_audio');
 *
 * // Mute/unmute
 * import { muteAudio, unmuteAudio, toggleMuteAudio } from './helper/audioHelper';
 * muteAudio();
 * unmuteAudio();
 * const isMuted = toggleMuteAudio();
 * ```
 *
 * Audio files should be listed in public/assets/data/audios.json:
 * {
 *   "example_audio": "./assets/audios/example_audio.mp3",
 *   "another_audio": "./assets/audios/another_audio.mp3"
 * }
 */
/**
 * Audio manager class to handle all audio operations
 */
var AudioManager = /** @class */ (function () {
    function AudioManager() {
        this.audioConfig = {};
        this.audioInstances = {};
        this.isMuted = false;
        this.isTabFocused = true;
        this.pausedAudios = new Set();
    }
    /**
     * Initialize audio manager with audio configuration from audios.json
     * @param config - Audio configuration object (from audios.json)
     */
    AudioManager.prototype.initialize = function (config) {
        var _this = this;
        this.audioConfig = config;
        // Create Howl instances for each audio
        Object.keys(config).forEach(function (key) {
            var audioInstance = {
                howl: new Howl({
                    src: [config[key]],
                    loop: false,
                    volume: 1.0,
                    onend: function (id) {
                        var inst = _this.audioInstances[key];
                        // Remove from active list
                        inst.activeSoundIds = inst.activeSoundIds.filter(function (sid) { return sid !== id; });
                        // Don't clear state if the sound is looping (onend fires each iteration)
                        if (!inst.howl.loop()) {
                            // Only mark as ended if this is the most recent sound instance.
                            // With overlap enabled, older sounds ending must not corrupt
                            // the shared state while a newer sound is still playing.
                            if (inst.soundId === id) {
                                inst.isEnded = true;
                                inst.soundId = undefined;
                            }
                        }
                    },
                    onloaderror: function (id, error) {
                        console.error("Failed to load audio \"".concat(key, "\":"), error);
                    },
                }),
                activeSoundIds: [],
                isEnded: false,
                wasPlayingBeforeBlur: false,
            };
            _this.audioInstances[key] = audioInstance;
        });
        // Set up tab focus/blur listeners
        this.setupTabFocusListeners();
    };
    /**
     * Set up event listeners for tab focus and blur
     */
    AudioManager.prototype.setupTabFocusListeners = function () {
        var _this = this;
        // Handle visibility change (tab switching) - most reliable method
        document.addEventListener('visibilitychange', function () {
            if (document.hidden) {
                _this.isTabFocused = false;
                _this.handleTabBlur();
            }
            else {
                _this.isTabFocused = true;
                _this.handleTabFocus();
            }
        });
        // Handle window blur/focus as fallback
        window.addEventListener('blur', function () {
            if (_this.isTabFocused) {
                _this.isTabFocused = false;
                _this.handleTabBlur();
            }
        });
        window.addEventListener('focus', function () {
            if (!_this.isTabFocused) {
                _this.isTabFocused = true;
                _this.handleTabFocus();
            }
        });
    };
    /**
     * Handle tab blur - pause all playing audios
     */
    AudioManager.prototype.handleTabBlur = function () {
        var _this = this;
        Object.keys(this.audioInstances).forEach(function (key) {
            var instance = _this.audioInstances[key];
            if (instance.soundId !== undefined && instance.howl.playing(instance.soundId)) {
                instance.wasPlayingBeforeBlur = true;
                instance.howl.pause(instance.soundId);
                _this.pausedAudios.add(key);
            }
        });
    };
    /**
     * Handle tab focus - resume previously playing audios
     */
    AudioManager.prototype.handleTabFocus = function () {
        var _this = this;
        if (!this.isMuted) {
            this.pausedAudios.forEach(function (key) {
                var instance = _this.audioInstances[key];
                // Only resume if audio hasn't ended and has a valid soundId
                if (instance &&
                    !instance.isEnded &&
                    instance.wasPlayingBeforeBlur &&
                    instance.soundId !== undefined) {
                    // Resume the paused audio
                    instance.howl.play(instance.soundId);
                    instance.wasPlayingBeforeBlur = false;
                }
            });
            this.pausedAudios.clear();
        }
    };
    /**
     * Play an audio by key
     * @param key - Audio key from audios.json
     * @param options - Optional playback options
     * @returns Sound ID if successful, undefined otherwise
     */
    AudioManager.prototype.play = function (key, options) {
        // Don't play if muted
        if (this.isMuted) {
            return undefined;
        }
        // Don't play if tab is not focused
        if (!this.isTabFocused) {
            return undefined;
        }
        var instance = this.audioInstances[key];
        if (!instance) {
            console.warn("Audio \"".concat(key, "\" not found"));
            return undefined;
        }
        // Don't play if audio has ended
        if (instance.isEnded) {
            instance.howl.seek(0);
        }
        // If already playing, stop it first (unless overlap is enabled)
        if (!(options === null || options === void 0 ? void 0 : options.overlap) &&
            instance.soundId !== undefined &&
            instance.howl.playing(instance.soundId)) {
            this.stop(key);
        }
        // Skip play if max overlap limit is reached — let existing sounds finish
        if ((options === null || options === void 0 ? void 0 : options.overlap) && options.maxOverlap && options.maxOverlap > 0) {
            if (instance.activeSoundIds.length >= options.maxOverlap) {
                return undefined;
            }
        }
        // Apply options if provided
        if ((options === null || options === void 0 ? void 0 : options.volume) !== undefined) {
            instance.howl.volume(options.volume);
        }
        if ((options === null || options === void 0 ? void 0 : options.loop) !== undefined) {
            instance.howl.loop(options.loop);
        }
        // Play the audio
        var soundId = instance.howl.play();
        instance.soundId = soundId;
        instance.activeSoundIds.push(soundId);
        instance.isEnded = false;
        instance.wasPlayingBeforeBlur = false;
        return soundId;
    };
    /**
     * Pause an audio by key
     * @param key - Audio key from audios.json
     */
    AudioManager.prototype.pause = function (key) {
        var instance = this.audioInstances[key];
        if (!instance) {
            console.warn("Audio \"".concat(key, "\" not found"));
            return;
        }
        if (instance.soundId !== undefined && instance.howl.playing(instance.soundId)) {
            instance.howl.pause(instance.soundId);
            this.pausedAudios.add(key);
        }
    };
    /**
     * Stop an audio by key
     * @param key - Audio key from audios.json
     */
    AudioManager.prototype.stop = function (key) {
        var instance = this.audioInstances[key];
        if (!instance) {
            console.warn("Audio \"".concat(key, "\" not found"));
            return;
        }
        // Always stop all sounds on this Howl (handles cases where soundId was
        // cleared by onend during a loop iteration)
        instance.howl.stop();
        instance.howl.loop(false);
        instance.soundId = undefined;
        instance.activeSoundIds = [];
        instance.isEnded = false;
        instance.wasPlayingBeforeBlur = false;
        this.pausedAudios.delete(key);
    };
    /**
     * Stop all playing audios
     */
    AudioManager.prototype.stopAll = function () {
        var _this = this;
        Object.keys(this.audioInstances).forEach(function (key) {
            _this.stop(key);
        });
    };
    /**
     * Pause all playing audios
     */
    AudioManager.prototype.pauseAll = function () {
        var _this = this;
        Object.keys(this.audioInstances).forEach(function (key) {
            _this.pause(key);
        });
    };
    /**
     * Mute all audios
     */
    AudioManager.prototype.mute = function () {
        var _this = this;
        this.isMuted = true;
        Object.keys(this.audioInstances).forEach(function (key) {
            var instance = _this.audioInstances[key];
            if (instance.soundId !== undefined && instance.howl.playing(instance.soundId)) {
                instance.howl.volume(0);
            }
        });
    };
    /**
     * Unmute all audios
     */
    AudioManager.prototype.unmute = function () {
        var _this = this;
        this.isMuted = false;
        Object.keys(this.audioInstances).forEach(function (key) {
            var instance = _this.audioInstances[key];
            // Always restore volume — mute() may have set it to 0 on a sound that has
            // since ended, leaving the Howl's default volume stuck at 0 for future plays.
            instance.howl.volume(1.0);
        });
    };
    /**
     * Toggle mute state
     * @returns New mute state
     */
    AudioManager.prototype.toggleMute = function () {
        if (this.isMuted) {
            this.unmute();
        }
        else {
            this.mute();
        }
        return this.isMuted;
    };
    /**
     * Get mute state
     * @returns Current mute state
     */
    AudioManager.prototype.getMuteState = function () {
        return this.isMuted;
    };
    /**
     * Check if an audio is currently playing
     * @param key - Audio key from audios.json
     * @returns True if playing, false otherwise
     */
    AudioManager.prototype.isPlaying = function (key) {
        var instance = this.audioInstances[key];
        if (!instance || instance.soundId === undefined) {
            return false;
        }
        return instance.howl.playing(instance.soundId);
    };
    /**
     * Check if an audio has ended
     * @param key - Audio key from audios.json
     * @returns True if ended, false otherwise
     */
    AudioManager.prototype.hasEnded = function (key) {
        var instance = this.audioInstances[key];
        if (!instance) {
            return false;
        }
        return instance.isEnded;
    };
    /**
     * Set volume for a specific audio
     * @param key - Audio key from audios.json
     * @param volume - Volume level (0.0 to 1.0)
     */
    AudioManager.prototype.setVolume = function (key, volume) {
        var instance = this.audioInstances[key];
        if (!instance) {
            console.warn("Audio \"".concat(key, "\" not found"));
            return;
        }
        var clampedVolume = Math.max(0, Math.min(1, volume));
        instance.howl.volume(clampedVolume);
    };
    /**
     * Get volume for a specific audio
     * @param key - Audio key from audios.json
     * @returns Volume level (0.0 to 1.0)
     */
    AudioManager.prototype.getVolume = function (key) {
        var instance = this.audioInstances[key];
        if (!instance) {
            return 0;
        }
        return instance.howl.volume();
    };
    /**
     * Reset an audio (allows it to be played again even if it ended)
     * @param key - Audio key from audios.json
     */
    AudioManager.prototype.reset = function (key) {
        var instance = this.audioInstances[key];
        if (!instance) {
            console.warn("Audio \"".concat(key, "\" not found"));
            return;
        }
        this.stop(key);
        instance.isEnded = false;
    };
    /**
     * Clean up all audio instances
     */
    AudioManager.prototype.cleanup = function () {
        var _this = this;
        this.stopAll();
        Object.keys(this.audioInstances).forEach(function (key) {
            var instance = _this.audioInstances[key];
            instance.howl.unload();
        });
        this.audioInstances = {};
        this.pausedAudios.clear();
    };
    return AudioManager;
}());
// Create singleton instance
var audioManager = new AudioManager();
/**
 * Initialize audio system with configuration from audios.json
 * @param config - Audio configuration object (from audios.json)
 */
export var initializeAudio = function (config) {
    audioManager.initialize(config);
};
/**
 * Play an audio by key
 * @param key - Audio key from audios.json
 * @param options - Optional playback options
 * @returns Sound ID if successful, undefined otherwise
 */
export var playAudio = function (key, options) {
    return audioManager.play(key, options);
};
/**
 * Pause an audio by key
 * @param key - Audio key from audios.json
 */
export var pauseAudio = function (key) {
    audioManager.pause(key);
};
/**
 * Stop an audio by key
 * @param key - Audio key from audios.json
 */
export var stopAudio = function (key) {
    audioManager.stop(key);
};
/**
 * Stop all playing audios
 */
export var stopAllAudios = function () {
    audioManager.stopAll();
};
/**
 * Pause all playing audios
 */
export var pauseAllAudios = function () {
    audioManager.pauseAll();
};
/**
 * Mute all audios
 */
export var muteAudio = function () {
    audioManager.mute();
};
/**
 * Unmute all audios
 */
export var unmuteAudio = function () {
    audioManager.unmute();
};
/**
 * Toggle mute state
 * @returns New mute state
 */
export var toggleMuteAudio = function () {
    return audioManager.toggleMute();
};
/**
 * Get mute state
 * @returns Current mute state
 */
export var getMuteState = function () {
    return audioManager.getMuteState();
};
/**
 * Check if an audio is currently playing
 * @param key - Audio key from audios.json
 * @returns True if playing, false otherwise
 */
export var isAudioPlaying = function (key) {
    return audioManager.isPlaying(key);
};
/**
 * Check if an audio has ended
 * @param key - Audio key from audios.json
 * @returns True if ended, false otherwise
 */
export var hasAudioEnded = function (key) {
    return audioManager.hasEnded(key);
};
/**
 * Set volume for a specific audio
 * @param key - Audio key from audios.json
 * @param volume - Volume level (0.0 to 1.0)
 */
export var setAudioVolume = function (key, volume) {
    audioManager.setVolume(key, volume);
};
/**
 * Get volume for a specific audio
 * @param key - Audio key from audios.json
 * @returns Volume level (0.0 to 1.0)
 */
export var getAudioVolume = function (key) {
    return audioManager.getVolume(key);
};
/**
 * Reset an audio (allows it to be played again even if it ended)
 * @param key - Audio key from audios.json
 */
export var resetAudio = function (key) {
    audioManager.reset(key);
};
/**
 * Clean up all audio instances
 */
export var cleanupAudio = function () {
    audioManager.cleanup();
};
